import { Group, Progress, Text } from "@mantine/core"
import { useForm } from "@mantine/form"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAPIAnswer } from "@/hooks"
import type { TSurveyAnswer } from "@/types"
import { QuestionAnimated } from "../questions"
import { ButtonSurvey } from "./StageButton"
import { StageControls } from "./StageControls"
import type { IStageQuestionProps } from "./types"
import { filterVisibleQuestions, validateQuestion } from "./utils"

export function QuestionsCommonStage({ answers, questions, usersession, onNext }: IStageQuestionProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [step, setStep] = useState(0)
  const [dir, setDir] = useState<"next" | "prev">("next")

  // Questions visible given the already-saved answers
  const baseVisible = filterVisibleQuestions(questions, answers)
  const question = baseVisible?.[step || 0]
  const answer = answers && question && answers.find((a) => a.question_id === question.id)

  // Get the user and the initial value
  // const { user } = useAuth()
  // const value = answer?.value ?? InitialPersonalValue(question, user!)
  const value = answer?.value

  // Create the form
  const form = useForm({
    validateInputOnBlur: true,
    validateInputOnChange: true,
    initialValues: { value: undefined, comment: undefined } as Pick<TSurveyAnswer, "comment" | "value">,
    validate: (values) => {
      const { value, comment } = values
      return (question && validateQuestion(question, value, comment, t)) || {}
    },
  })

  useEffect(() => {
    form.setFieldValue("value", value)
    form.setFieldValue("comment", answer?.comment ?? "")
    form.resetDirty()
  }, [answer, question])

  // Reveal the current branch question's dependents once the user picks an answer,
  // so "Next" appears instead of "Finish" while on a branch question.
  const visibleQuestions =
    question &&
    form.isDirty() &&
    (question.type === "boolean" || question.type === "select") &&
    form.values.value != null
      ? filterVisibleQuestions(questions, [...answers, { question_id: question.id, value: form.values.value }])
      : baseVisible

  // Clamp step when a changed branch answer hides the tail of the visible list
  useEffect(() => {
    if (visibleQuestions.length && step >= visibleQuestions.length) setStep(visibleQuestions.length - 1)
  }, [visibleQuestions.length, step])

  const { create, isUpdating } = useAPIAnswer(usersession.session.id)

  if (!question) return null
  const isReady = !isUpdating && (!question.is_required || form.isValid())

  return (
    <>
      <Group wrap="nowrap" gap="xs">
        <Progress
          w="100%"
          size="lg"
          color="gray"
          transitionDuration={200}
          value={((step + 1) / visibleQuestions.length) * 100}
        />
        <Text fw={500} c="gray" fz="md">
          {step + 1}/{visibleQuestions.length}
        </Text>
      </Group>
      {question && (
        <QuestionAnimated
          dir={dir}
          idx={step}
          question={question}
          value={form.values.value}
          comment={form.values.comment}
          setValue={(value) => form.setFieldValue("value", value)}
          setComment={(comment) => form.setFieldValue("comment", comment)}
          field={form.getInputProps(question.type == "string" ? "comment" : "value")}
        />
      )}
      <StageControls>
        {/* Previous button is only shown on questions that are not the first question */}
        <ButtonSurvey
          mr="auto"
          flex={1}
          color="gray"
          variant="navy"
          disabled={!step}
          onClick={() => {
            setDir("prev")
            setStep(step - 1)
          }}
        >
          {t("Previous")}
        </ButtonSurvey>
        {/* Next button is only shown on questions that are not the last question */}
        <ButtonSurvey
          flex={2}
          disabled={!isReady}
          variant={form.isDirty() ? "save" : "navy"}
          hidden={step === visibleQuestions.length - 1}
          onClick={async () => {
            setDir("next")
            if (form.isDirty() || question.type == "personal") {
              await create([
                {
                  ...answer,
                  value: form.values.value,
                  question_id: question.id,
                  comment: form.values.comment,
                  usersession_id: usersession.id,
                },
              ])

              queryClient.invalidateQueries({
                queryKey: ["answers", { where: { usersession_id: usersession.id } }],
              })
            }

            form.reset()
            setStep(step + 1)
          }}
        >
          {t("Next")}
        </ButtonSurvey>
        {/* Finish button is only shown on the last question */}
        <ButtonSurvey
          flex={2}
          variant="save"
          disabled={!isReady}
          hidden={step !== visibleQuestions.length - 1}
          onClick={async () => {
            if (form.isDirty() || question.type == "personal")
              await create([
                {
                  ...answer,
                  value: form.values.value,
                  question_id: question.id,
                  comment: form.values.comment,
                  usersession_id: usersession.id,
                },
              ])
            if (onNext) onNext()
          }}
        >
          {t("Finish")}
        </ButtonSurvey>
      </StageControls>
    </>
  )
}

// function InitialPersonalValue(question: TSurveyQuestion | undefined, user: TUser) {
//   if (!question || question.type !== "personal") return
//
//   const category = question.meta.category
//
//   if (category == "manager") return user.manager_id ? parseInt(user.manager_id, 10) : undefined
//   if (category == "city") return user.city_id ? parseInt(user.city_id, 10) : undefined
//   if (category == "birthday") return user.birthday ? new Date(user.birthday).valueOf() / 1000 : undefined
//   if (category == "start_date") return user.dtstart ? new Date(user.dtstart).valueOf() / 1000 : undefined
//
//   return undefined
// }
